/**
 * CB's P&L line items are a TREE, and the plain Profit & Loss statement has to
 * render all of it (CC-50249).
 *
 * cb2 shipped a renderer that read only `section.lineItems` — the top level —
 * so every child account was invisible and the rows on screen did not add up to
 * the section footer beside them. Live on vladdemo (all dates, accrual):
 *
 *   Income    4 rows summing 15,337,886.19 under a footer of 17,999,172.36
 *   Expenses 14 rows summing  1,326,545.38 under a footer of 12,095,800.25
 *
 * …with 41 accounts (9 + 32) never drawn at all. Net Income was right, because
 * it comes from the server, so the statement looked fine while its body did not
 * explain it. That is the worst failure mode a financial statement has.
 *
 * ─── Payload facts, captured live from GetPLReport on vladdemo 2026-08-10 ────
 * Verified against real responses, not inferred from the C#.
 *
 * 1. Every line item carries `childLines` (possibly empty) and `isParent`. The
 *    tree is genuinely more than two levels deep — Expenses reaches depth 2
 *    (Client Related Costs › Document Production › Copying).
 *
 * 2. `amount` is an account's OWN figure; `totalAmount` is its roll-up:
 *        totalAmount === amount + Σ(child totalAmount)
 *    Checked across all 10 parents in the live payload: 0 mismatches.
 *    On a leaf the two are equal (checked across all 51 leaves).
 *
 * 3. THE RECONCILIATION IDENTITY THIS MODULE EXISTS TO PRESERVE:
 *        Σ(`amount` over EVERY node in the tree) === section.sectionTotal
 *    Live delta was exactly 0.000000 in all four sections. Equivalently,
 *    Σ(`totalAmount` over TOP-LEVEL nodes) === sectionTotal — which is why the
 *    server was self-consistent all along and the omission was ours.
 *
 *    ⇒ Rendering children AND using `totalAmount` on their parent would double
 *      count every child. Each account row must show `amount`; only the
 *      "Total {parent}" line shows `totalAmount`.
 *
 * 4. The same identity holds for the comparison figures: child rows carry
 *    `comparisonAmount` / `comparisonAmountDiff`, and a parent's roll-up lives
 *    in `totalComparisonAmount` / `totalComparisonAmountDiff`.
 *
 * ─── V1 parity ──────────────────────────────────────────────────────────────
 * `AccountingPAndLReportView.js:955-1002` (`addAccountRow`): render the account
 * with its OWN amount, recurse over `ChildLines` in payload order, then — only
 * if it had children — append a `Total {AccountName}` line carrying
 * `TotalAmountString`. Rows are emitted FLAT into one table; V1's indentation is
 * a treegrid CSS concern, not a data structure. This module reproduces that
 * sequence exactly, and reports `depth` so the caller can indent.
 *
 * Accounts with `accountId === -999` are skipped at every level, as V1 does
 * (`:959`). That sentinel does not appear in vladdemo's current data, so this is
 * parity insurance rather than an observed fix.
 *
 * NOTE: V1's ACCOUNT-SEGMENT renderer (`addAccountSegmentRow`, `:812`) very
 * deliberately does NOT recurse — it draws one row per top-level account using
 * `TotalAmountString`. So the Account-Seg / Category matrix (`segmentMatrix.js`)
 * is correct as it stands and must NOT be given tree handling. The two modes
 * reconcile to the same sectionTotal by different routes; that is by design.
 */

/** Rows this module can emit. `account` = a real account line (renders its own
 *  `amount`); `total` = the "Total {parent}" roll-up line (renders
 *  `totalAmount`). Only `account` rows participate in the reconciliation. */
export const ROW_ACCOUNT = 'account'
export const ROW_TOTAL = 'total'

/** V1 skips this sentinel account at every level (`:824`, `:959`). */
export const SENTINEL_ACCOUNT_ID = -999

/** Defence in depth against a malformed payload: a cycle in `childLines` would
 *  otherwise hang the browser on a financial statement. The ancestor set below
 *  catches true cycles; this caps pathological-but-acyclic nesting too. */
export const MAX_TREE_DEPTH = 32

/**
 * Flatten CB's account tree into V1's row sequence.
 *
 * Spec 127 §4.2 (S4): every emitted row also carries `parentId` — the
 * `accountId` of its nearest RENDERED ancestor, `null` at the top of its section.
 * A child is only ever walked from a parent that rendered (a skipped −999 account
 * takes its subtree with it), so that ancestor is always the row's own parent. A
 * `total` row carries its account's `parentId` (it closes the account, so it sits
 * where the account sits). Order, kinds, the −999 skip, the depth cap and the cycle
 * guard are unchanged. The statement builder keys roll-up on these ids and on the
 * presence of a `total` row — never on CB's `isParent`, which is `false` on a
 * Comparative parent whose children exist only in the comparison period (measured:
 * Rent 121 with 8 children, 01/01–05/15/2024 cash, Prior Period).
 *
 * @param {Array} lineItems `section.lineItems`
 * @returns {Array<{item: object, depth: number, kind: 'account'|'total', parentId: (number|string|null)}>}
 *   in render order: parent, its descendants, then the parent's Total line.
 */
export function flattenPLAccounts(lineItems = []) {
  const out = []
  // Ancestors on the CURRENT path only — a legitimate payload may repeat an
  // object elsewhere in the tree, but revisiting one of your own ancestors is a
  // cycle. Tracked by identity, so it works whether or not accountId is unique.
  const path = new Set()

  const walk = (item, depth, parentId) => {
    if (!item || typeof item !== 'object') return
    if (Number(item.accountId) === SENTINEL_ACCOUNT_ID) return
    if (depth > MAX_TREE_DEPTH || path.has(item)) return

    out.push({ item, depth, kind: ROW_ACCOUNT, parentId })

    const children = Array.isArray(item.childLines) ? item.childLines.filter(Boolean) : []
    if (children.length === 0) return

    const before = out.length
    path.add(item)
    for (const child of children) walk(child, depth + 1, item.accountId ?? null)
    path.delete(item)

    // V1 emits the roll-up line for an account that HAS children (`:984-1000`).
    // We additionally require that at least one of them actually rendered. The
    // difference only bites when every child was skipped (all -999, or a cycle),
    // and there V1 would draw a "Total {parent}" with nothing above it to
    // explain it — an orphan subtotal on a financial statement. A leaf never
    // gets a Total line either way.
    if (out.length > before) out.push({ item, depth, kind: ROW_TOTAL, parentId })
  }

  for (const item of Array.isArray(lineItems) ? lineItems : []) walk(item, 0, null)
  return out
}

/**
 * Σ of the figures the ACCOUNT rows put on screen. This is the number that must
 * equal `section.sectionTotal`; the guard for fact 3 above, and the assertion
 * that would have caught CC-50249 on day one.
 *
 * @param {Array} rows output of `flattenPLAccounts`
 * @param {(item: object) => number|string} pick which server field the row shows
 */
export function sumRenderedAccounts(rows = [], pick = (item) => item.amount) {
  return rows
    .filter((r) => r.kind === ROW_ACCOUNT)
    .reduce((acc, r) => acc + (Number(pick(r.item)) || 0), 0)
}

export default flattenPLAccounts
